import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { Avatar } from '@/presentation/components/Avatar';
import { BackButton } from '@/presentation/components/BackButton';
import { useEmployeesByAccessRoleQuery } from '@/modules/employees/application/employeeQueries';
import type { Employee } from '@/modules/employees/domain/entities/Employee';
import { MethodChips } from '../components/MethodChips';
import { capitalizeIfUpper } from '@/shared/utils/mapping';
import { GlassLoader } from '@/shared/ui/components/GlassLoader';

export function AdminEmployeesPage() {
  const { C, T } = useTheme();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { selectedRole } = useProfile();
  const [q, setQ] = useState('');

  const { data: employees = [], isLoading, isFetching } = useEmployeesByAccessRoleQuery(
    selectedRole?.idAccessRole ?? null,
  );

  const filtered = useMemo(() => {
    const lq = q.trim().toLowerCase();
    if (!lq) return employees;
    return employees.filter(
      (e) =>
        e.fullName.toLowerCase().includes(lq) ||
        e.code.toLowerCase().includes(lq) ||
        e.structureName.toLowerCase().includes(lq),
    );
  }, [employees, q]);

  const goToDetail = (emp: Employee) => {
    void navigate(`/admin/empleados/${emp.idEntity}`, { state: { employee: emp } });
  };

  return (
    <div style={{ flex: 1, background: T.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '24px 30px 18px', borderBottom: `1px solid ${T.border}`, background: T.surfaceBg, flexShrink: 0 }}>
        <div style={{ maxWidth: 645, margin: '0 auto', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, position: 'relative' }}>
            <BackButton T={T} onClick={() => { void navigate('/admin'); }} />
            <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.text, pointerEvents: 'none' }}>{t('admin.employeesTitle')}</div>
          </div>
          <div style={{ position: 'relative' }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); }}
              placeholder={t('admin.searchPlaceholder')}
              style={{ width: '100%', padding: '9px 12px 9px 32px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.rowBg, color: T.text, fontFamily: 'inherit', fontSize: 11, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ fontSize: 9, color: T.textMuted, marginTop: 8, fontWeight: 600 }}>
            {t('admin.employeesCount', { filtered: filtered.length, total: employees.length })}
          </div>
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 30px 30px' }}>
        <div style={{ maxWidth: 645, margin: '0 auto', position: 'relative' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '45px 0', color: T.textMuted, fontSize: 11 }}>{t('admin.loadingEmployees')}</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '45px 0', color: T.textMuted, fontSize: 11 }}>{t('admin.noEmployees')}</div>
          ) : (
            filtered.map((emp) => (
              <div
                key={emp.idPerson}
                onClick={() => { goToDetail(emp); }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 15px', borderRadius: 11, cursor: 'pointer', background: T.surfaceBg, border: `1px solid ${T.border}`, marginBottom: 7, transition: 'all .15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.rowBg; e.currentTarget.style.borderColor = C.primary + '44'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = T.surfaceBg; e.currentTarget.style.borderColor = T.border; }}
              >
                <Avatar initials={emp.initials} color={emp.avatarColor} size={34} photoUrl={emp.photoUrl ?? null} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{emp.fullName}</div>
                  {emp.cargo && (
                    <div style={{ fontSize: 9, color: T.textMuted, marginTop: 2 }}>
                      {capitalizeIfUpper(emp.cargo)}
                    </div>
                  )}
                </div>
                <MethodChips methods={emp.enrolledMethods} T={T} C={C} size="sm" />
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
              </div>
            ))
          )}
          {isFetching && <GlassLoader T={T} />}
        </div>
      </div>
    </div>
  );
}
